import { useState, useEffect, useCallback } from "react";
import {
  Search,
  Filter,
  Download,
  Copy,
  Eye,
  Calendar,
  CreditCard,
  Smartphone,
  Wallet as WalletIcon,
  Building,
} from "lucide-react";
import { Card } from "@/ui/components/primitives/card";
import { Button } from "@/ui/components/primitives/button";
import { Input } from "@/ui/components/primitives/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/ui/components/primitives/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/components/primitives/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/primitives/select";
import { StatusBadge } from "@/modules/shared/components/status-badge";
import { TransactionTypeBadge } from "@/modules/shared/components/transaction-type-badge";
import { fetchTransactions } from "@/services/api/services/transactions";
import type { TransactionsResponse, TransactionFilters } from "@/modules/shared/types";
import { Badge } from "@/ui/components/primitives/badge";
import { useNavigate } from "react-router-dom";
import { downloadCSV } from "@/services/export";
import { TransactionDetailModal } from "@/modules/transactions/components/transaction-detail-modal";
import { format } from "date-fns";

const paymentMethodIcons = {
  UPI: Smartphone,
  Card: CreditCard,
  "Bank Transfer": Building,
  "Net Banking": Building,
  Wallet: WalletIcon,
};

export function TransactionsPage() {
  const navigate = useNavigate();

  // State for filters
  const [filters, setFilters] = useState<TransactionFilters>({
    page: 1,
    limit: 10,
    type: "all", // all, payin, payout
    status: "all-status",
    dateFrom: "",
    dateTo: "",
  });

  // UI State
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Data State
  const [data, setData] = useState<TransactionsResponse | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState<any>(null);

  // Debounced search
  const [searchDebounce, setSearchDebounce] = useState("");

  // Update search query with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchQuery);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when filters change
  const updateFilters = useCallback((newFilters: Partial<TransactionFilters>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1, // Reset to page 1 when filters change
    }));
  }, []);

  // Load transactions
  const loadTransactions = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const apiFilters: TransactionFilters = {
        ...filters,
        search: searchDebounce || undefined,
      };

      // Remove empty filters
      Object.keys(apiFilters).forEach(key => {
        if (apiFilters[key as keyof TransactionFilters] === "" ||
            apiFilters[key as keyof TransactionFilters] === undefined) {
          delete apiFilters[key as keyof TransactionFilters];
        }
      });

      const response = await fetchTransactions(apiFilters);
      setData(response);
    } catch (e: any) {
      setError("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [filters, searchDebounce]);

  // Load data when filters or search changes
  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newSize: number) => {
    setFilters(prev => ({ ...prev, limit: newSize, page: 1 }));
  };

  // Handle filter changes
  const handleTypeChange = (type: string) => {
    updateFilters({ type });
  };

  const handleStatusChange = (status: string) => {
    updateFilters({ status });
  };

  const handleDateRangeChange = (dateFrom: string, dateTo: string) => {
    updateFilters({ dateFrom, dateTo });
  };

  // Export functionality
  const handleExport = () => {
    if (data?.transactions) {
      downloadCSV(data.transactions, `transactions_${filters.type}_${new Date().toISOString().slice(0, 10)}.csv`);
    }
  };

  // Format amount helper
  const formatAmount = (n: number, cur?: string) => {
    const v = (n ?? 0).toLocaleString();
    if (!cur || cur === "INR") return `₹${v}`;
    if (cur === "USD") return `$${v}`;
    if (cur === "USDT" || cur === "CRYPTO") return `${v} ${cur}`;
    return `${v} ${cur}`;
  };

  // Copy to clipboard helper
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
    }
  };

  const stats = data?.summary || {
    totalCount: 0,
    successCount: 0,
    failedCount: 0,
    pendingCount: 0,
  };

  const pagination = data?.pagination || {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  };

  const transactions = data?.transactions || [];

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div>
        <h1>Transaction Book</h1>
        <p className="text-muted-foreground mt-1">
          View and manage all your payment transactions
        </p>
      </div>

      {/* Filters Card */}
      <Card className="p-6">
        <Tabs value={filters.type} onValueChange={handleTypeChange}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <TabsList className="flex-wrap">
              <TabsTrigger value="all" className="gap-2">
                All
                <Badge variant="secondary" className="rounded-full">
                  {stats.totalCount}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="payin" className="gap-2">
                <span className="text-success">↓</span> Payin Orders
                <Badge variant="secondary" className="rounded-full">
                  {stats.successCount}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="payout" className="gap-2">
                <span className="text-destructive">↑</span> Payout Orders
                <Badge variant="secondary" className="rounded-full">
                  {stats.pendingCount}
                </Badge>
              </TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={!transactions.length}
              >
                <Download className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Export CSV
              </Button>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative lg:col-span-2">
              <Search
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                strokeWidth={1.5}
              />
              <Input
                placeholder="Search by ID or customer name..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <Select value={filters.status} onValueChange={handleStatusChange}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-status">All Status</SelectItem>
                <SelectItem value="SUCCESS">Success</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="PROCESSING">Processing</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
                <SelectItem value="EXPIRED">Expired</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <div className="lg:col-span-2">
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  placeholder="From"
                  value={filters.dateFrom || ""}
                  onChange={(e) => {
                    const dateFrom = e.target.value;
                    handleDateRangeChange(dateFrom, filters.dateTo || "");
                  }}
                  className="h-11"
                />
                <span className="text-sm text-muted-foreground">to</span>
                <Input
                  type="date"
                  placeholder="To"
                  value={filters.dateTo || ""}
                  onChange={(e) => {
                    const dateTo = e.target.value;
                    handleDateRangeChange(filters.dateFrom || "", dateTo);
                  }}
                  className="h-11"
                />
              </div>
            </div>
          </div>
        </Tabs>
      </Card>

      {/* Transactions Table */}
      <Card className="md:block hidden">
        <div className="border rounded-lg md:overflow-x-auto overflow-x-hidden">
          <Table aria-label="Transactions table">
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Reference ID</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Fee</TableHead>
                <TableHead className="text-right">Net</TableHead>
                <TableHead>Date & Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      <p className="text-muted-foreground">Loading transactions...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <p className="text-muted-foreground">No transactions found</p>
                      <p className="text-sm text-muted-foreground">
                        Try adjusting your filters
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((transaction) => {
                  const MethodIcon =
                    paymentMethodIcons[
                      transaction.method as keyof typeof paymentMethodIcons
                    ] || CreditCard;

                  return (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        <TransactionTypeBadge type={transaction.type} />
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        <button
                          className="hover:text-primary transition-colors"
                          aria-label={`View transaction ${transaction.referenceId}`}
                          onClick={() => {
                            setSelectedTxn(transaction);
                            setDetailOpen(true);
                          }}
                        >
                          {transaction.referenceId}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                            <MethodIcon className="h-4 w-4" strokeWidth={1.5} />
                          </div>
                          <span className="text-sm">{transaction.method}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={transaction.status} />
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatAmount(transaction.amount, transaction.currency)}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatAmount(transaction.fee, transaction.currency)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatAmount(transaction.net, transaction.currency)}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{transaction.date}</p>
                          <p className="text-xs text-muted-foreground">
                            {transaction.time}
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between p-4 border-t">
          <p className="text-sm text-muted-foreground">
            Showing {transactions.length} of {pagination.total} transactions
            {pagination.total > 0 && ` (Page ${pagination.page} of ${pagination.pages})`}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              Previous
            </Button>
            <span className="text-sm">Page {pagination.page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.pages}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Next
            </Button>
            <Select
              value={String(pagination.limit)}
              onValueChange={(v) => handlePageSizeChange(parseInt(v))}
            >
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Transactions Card List - mobile */}
      <Card className="md:hidden">
        <div className="space-y-3 p-3">
          {loading ? (
            <div className="text-center text-muted-foreground py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p>Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">No transactions found</div>
          ) : (
            transactions.map((t) => (
              <div key={t.id} className="p-4 border rounded-lg grid gap-3">
                <div className="flex items-center justify-between">
                  <TransactionTypeBadge type={t.type} />
                  <StatusBadge status={t.status} />
                </div>
                <div className="grid gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Reference ID</span>
                    <button
                      className="font-mono text-sm hover:text-primary transition-colors break-all text-right"
                      aria-label={`View transaction ${t.referenceId}`}
                      onClick={() => navigate(`/transactions/${t.id}`)}
                    >
                      {t.referenceId}
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Method</span>
                    <span className="text-sm">{t.method}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Amount</span>
                    <span className="font-semibold">{formatAmount(t.amount, t.currency)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Date</span>
                    <span className="text-sm">{t.date} {t.time}</span>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label="Copy ID"
                    onClick={() => copyToClipboard(t.referenceId)}
                  >
                    <Copy className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label="View Details"
                    onClick={() => {
                      setSelectedTxn(t);
                      setDetailOpen(true);
                    }}
                  >
                    <Eye className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Mobile Pagination */}
        <div className="flex items-center justify-center p-4 border-t gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page <= 1}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            Previous
          </Button>
          <span className="text-sm px-2">Page {pagination.page} of {pagination.pages}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page >= pagination.pages}
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            Next
          </Button>
        </div>
      </Card>

      <TransactionDetailModal open={detailOpen} onOpenChange={setDetailOpen} txn={selectedTxn} />
    </div>
  );
}